import { skey } from '../alg/keys';

export class StructMap<K, V> implements Map<K, V> {
  #map = new Map<string, [K, V]>();

  readonly keyspace: (key: K) => string;

  constructor(keyspace: (key: K) => string = skey) {
    this.keyspace = keyspace;
  }

  /**
   * Removes all elements from the Map.
   */
  clear(): void {
    this.#map.clear();
  }

  /**
   * @returns true if an element in the Map existed and has been removed, or false if the element does not exist.
   */
  delete(key: K): boolean {
    return this.#map.delete(this.keyspace(key));
  }

  /**
   * Executes a provided function once per each key/value pair in the Map, in insertion order.
   */
  forEach(
    callbackfn: (value: V, key: K, map: Map<K, V>) => void,
    thisArg?: any
  ): void {
    this.#map.forEach(([key, value]) => {
      callbackfn.apply(thisArg, [value, key, this]);
    });
  }

  *entries(): MapIterator<[K, V]> {
    for (const [key, value] of this.#map.values()) {
      yield [key, value];
    }
  }

  *keys(): MapIterator<K> {
    for (const [key, __] of this.#map.values()) {
      yield key;
    }
  }

  *values(): MapIterator<V> {
    for (const [__, value] of this.#map.values()) {
      yield value;
    }
  }

  [Symbol.iterator]() {
    return this.entries();
  }

  get [Symbol.toStringTag]() {
    return 'StructMap';
  }

  /**
   * Returns a specified element from the Map object. If the value that is associated to the provided key is an object, then you will get a reference to that object and any change made to that object will effectively modify it inside the Map.
   * @returns Returns the element associated with the specified key. If no element is associated with the specified key, undefined is returned.
   */
  get(key: K): V | undefined {
    return this.#map.get(this.keyspace(key))?.[1];
  }

  /**
   * @returns boolean indicating whether an element with the specified key exists or not.
   */
  has(key: K): boolean {
    return this.#map.has(this.keyspace(key));
  }

  /**
   * Adds a new element with a specified key and value to the Map. If an element with the same key already exists, the element will be updated.
   */
  set(key: K, value: V): this {
    const k = this.keyspace(key);
    const existing = this.#map.get(k);
    if (existing !== undefined) {
      existing[0] = key;
      existing[1] = value;
      return this;
    }
    this.#map.set(k, [key, value]);
    return this;
  }

  /**
   * Returns the value for `key`, first inserting `value` if there is none.
   */
  getOrInsert(key: K, value: V): V {
    const k = this.keyspace(key);
    const existing = this.#map.get(k);
    if (existing !== undefined) return existing[1];
    this.#map.set(k, [key, value]);
    return value;
  }

  /**
   * Returns the value for `key`, first inserting `callbackfn(key)` if there is
   * none. The callback only runs when the key is missing.
   */
  getOrInsertComputed(key: K, callbackfn: (key: K) => V): V {
    const k = this.keyspace(key);
    const existing = this.#map.get(k);
    if (existing !== undefined) return existing[1];
    const value = callbackfn(key);
    this.#map.set(k, [key, value]);
    return value;
  }

  /**
   * @returns the number of elements in the Map.
   */
  get size() {
    return this.#map.size;
  }
}
